import { Collapse, IconButton } from '@mui/material'
import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import { text_colorTextSecondary } from 'ext-ui/style/colors'
import AiStarsIconV2 from 'ext-ui/svg/icon/AiStarsIconV2'
import SendIcon from 'ext-ui/svg/icon/SendIcon'
import React, { useEffect, useRef, useState } from 'react'
import { TransitionGroup } from 'react-transition-group'
import AgentPromptButton from 'src/agent/components/AgentPromptButton/AgentPromptButton'
import SvgClose from 'src/components/Svgs/SvgClose'
import { useTranslate } from 'src/localization/Trns'
import {
  InterruptOption,
  InterruptResolution,
  PromptTemplate,
} from 'src/queries/agent/network/agent.models'

import styles from './AgentChatField.style'

interface AgentChatFieldProps {
  value: string
  options: InterruptOption[] | undefined
  onOptionClick: (action: InterruptResolution, prompt: string) => void
  suggestedPrompts: PromptTemplate[] | undefined
  onSuggestedPromptClick: (promptTemplate: PromptTemplate) => void
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder?: string
  disabled?: boolean
  loading?: boolean
}

export type AgentChatFieldInputState =
  | 'freetext'
  | 'buttons'
  | 'feedback'
  | 'buttonsAndFeedback'

const isButtonState = (inputState: AgentChatFieldInputState): boolean => {
  return inputState === 'buttons' || inputState === 'buttonsAndFeedback'
}

const isTextState = (inputState: AgentChatFieldInputState): boolean => {
  return (
    inputState === 'freetext' ||
    inputState === 'feedback' ||
    inputState === 'buttonsAndFeedback'
  )
}

const isFeedbackState = (inputState: AgentChatFieldInputState): boolean => {
  return inputState === 'feedback' || inputState === 'buttonsAndFeedback'
}

const AgentChatField: React.FC<AgentChatFieldProps> = (props) => {
  const translate = useTranslate()
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      props.onSubmit()
    }
    if (event.key === 'Escape' && isFeedbackState(inputState)) {
      blurFeedback()
    }
  }

  const disableUserInput = props.disabled || props.loading

  const [inputState, setInputState] =
    useState<AgentChatFieldInputState>('freetext')

  const blurFeedback = (): void => {
    props.onChange('')
    textAreaRef.current?.blur()
    setInputState('buttons')
  }

  useEffect(() => {
    if (!props.options || props.options.length === 0) {
      setInputState('freetext')
    } else {
      setInputState('buttons')
    }
  }, [props.options])

  useEffect(() => {
    if (inputState === 'freetext') return

    if (props.value.length > 0) {
      setInputState('feedback')
    } else if (inputState === 'feedback' && props.value.length === 0) {
      setInputState('buttonsAndFeedback')
    }
  }, [props.value])

  return (
    <TransitionGroup css={styles} className="AgentChatField">
      {inputState === 'freetext' &&
        props.value.length === 0 &&
        props.suggestedPrompts &&
        props.suggestedPrompts.length > 0 && (
          <Collapse key={'AgentSuggestedPromptsWrapper'}>
            <div className="suggested-prompts-wrapper">
              {props.suggestedPrompts.map((promptTemplate, i) => {
                const prompt =
                  'displayPromptSrc' in promptTemplate
                    ? translate(promptTemplate.displayPromptSrc)
                    : promptTemplate.displayPrompt
                return (
                  <AgentPromptButton
                    key={(promptTemplate.promptId || prompt) + i}
                    prompt={prompt}
                    typingSpeed={20}
                    icon={<AiStarsIconV2 width={16} height={16} />}
                    onClick={(): void => {
                      props.onSuggestedPromptClick(promptTemplate)
                    }}
                  />
                )
              })}
            </div>
          </Collapse>
        )}
      {isButtonState(inputState) &&
        props.options &&
        props.options.length > 0 && (
          <Collapse key={'AgentButtonWrapper'}>
            <div className="buttons-wrapper">
              {props.options.map((option) => {
                let variant: 'primary' | 'severe' | 'spotlight' = 'primary'
                switch (option.action) {
                  case InterruptResolution.APPROVE:
                    variant = 'primary'
                    break
                  case InterruptResolution.REJECT:
                    variant = 'severe'
                    break
                  case InterruptResolution.NATURAL_LANGUAGE_FEEDBACK:
                    variant = 'spotlight'
                    break
                }
                return (
                  <Button
                    className="chat-button"
                    variant={variant}
                    key={option.action}
                    onClick={(): void => {
                      if (
                        option.action ===
                        InterruptResolution.NATURAL_LANGUAGE_FEEDBACK
                      ) {
                        setInputState('buttonsAndFeedback')
                        setTimeout(() => {
                          textAreaRef.current?.focus()
                        }, 0)
                      } else {
                        props.onOptionClick(option.action, option.label)
                      }
                    }}
                    disabled={disableUserInput}
                  >
                    {option.label}
                  </Button>
                )
              })}
            </div>
          </Collapse>
        )}
      {isTextState(inputState) && (
        <Collapse key={'AgentTextAreaWrapper'}>
          <div className="flex-row gap-2">
            <div
              className={cn('textarea-wrapper', {
                'with-close-button': isFeedbackState(inputState),
              })}
            >
              {isFeedbackState(inputState) && !props.loading && (
                <IconButton
                  className="close-button"
                  size="small"
                  disabled={disableUserInput}
                  onClick={(): void => blurFeedback()}
                >
                  <SvgClose stroke={text_colorTextSecondary} />
                </IconButton>
              )}
              <textarea
                ref={textAreaRef}
                className={'textarea'}
                value={props.value}
                onChange={(e): void => {
                  props.onChange(e.target.value)
                  e.currentTarget.style.height = 'auto' // reset height before setting, enables shrinking
                  e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`
                }}
                onKeyDown={handleKeyDown}
                onBlur={(): void => {
                  if (isFeedbackState(inputState) && props.value.length === 0) {
                    blurFeedback()
                  }
                }}
                placeholder={props.loading ? '' : props.placeholder}
                disabled={disableUserInput}
                rows={1}
              />
            </div>
            <Button
              className="send-button"
              dropShadow={false}
              onClick={(): void => props.onSubmit()}
              disabled={disableUserInput || props.value.trim().length === 0}
            >
              <SendIcon />
            </Button>
          </div>
        </Collapse>
      )}
    </TransitionGroup>
  )
}

export default AgentChatField
